import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
  getRegionId,
  getSecurityId,
  getSecurityCompanyName,
} from 'reducers/users';
import { totalMediafilesAllowed } from 'reducers/securityCompany';
import {
  eventsGridActions,
  eventSelectors,
  eventsNS,
} from 'containers/Pages/Events';
import BaseForm from 'containers/Base/BaseForm';
import { transformEventObject } from 'utils/DataTransform/Events/Form/EventObject';
import staticText from 'static/text_display.json';
import {
  initialValues,
  validationSchema,
  noSectionFields,
  severityRatingEnums,
  auditTrailSection,
  overviewSection,
  locationSection,
  descriptionSection,
  detailedSection,
  incidentReviewSection,
} from './options';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { EventInfo } from 'utils/constants/informationText';
import { HelpLinks } from 'utils/constants/helpLinks';
import COLORS from 'utils/constants/colors';
import isEmpty from 'lodash/isEmpty';
import { toggleModal } from 'reducers/ui';

const EventsForm = ({
  editMode,
  details,
  closeForm,
  creator,
  regionId,
  createEvent,
  updateEvent,
  securityCompanyId,
  securityCompanyName,
  subscribedCompanyServices,
  parentEventId,
  // from followupButtonComponent
  followupEventTitle,
  isFollowup,
  createFollowupEvent,
  disablePresist,
  persistentStateName,
  //--------------------
  size,
  defaultFieldValues,
  toggleModal,
}) => {
  const [startTime, setStartTime] = useState();
  const [finishTime, setFinishTime] = useState();
  const [eventCategory, setEventCategory] = useState('');
  const [media, setMedia] = useState();
  const [auditStatus, setAuditstatus] = useState();
  const [eventSitePolygon, setEventSitePolygon] = useState();

  const eventInitialState = {
    eventCategory: '',
    eventType: '',
    credibility: 5,
    reporters: [],
    involvedPeople: [],
    managerialOnlyDistribution: false,
    title: '',
    severityRating: severityRatingEnums.notRated,
    ...(!isEmpty(defaultFieldValues) && { ...defaultFieldValues }),
    ...(!isEmpty(defaultFieldValues) && {
      disabledFields: Object.keys(defaultFieldValues),
    }),
  };
  const handleFormValueUpdate = values => {
    if (values.locations) {
      values.locations.forEach(item => {
        item['color'] =
          values.eventCategory === 'News' ? COLORS.RED_01 : COLORS.BLUE_01;
      });
    }
    if (values.site) {
      setEventSitePolygon(values.site.boundary);
    } else {
      setEventSitePolygon(null);
    }
    if (values.startTime) {
      setStartTime(values.startTime);
    }
    if (values.eventCategory) {
      setEventCategory(values.eventCategory);
    } else if (values.eventType) {
      values['eventType'] = '';
      setEventCategory(values.eventCategory);
    }
    if (values.auditStatus) {
      setAuditstatus(values.auditStatus);
    }
    if (values.finishTime) {
      setFinishTime(values.finishTime);
    }
    if (values.media && values.media.length > 0) {
      setMedia(values.media);
    }
  };

  const onCreate = vals => {
    const submittedData = transformEventObject({
      ...vals,
      creator,
      regionId,
      securityCompanyId,
      securityCompanyName,
    });
    if (isFollowup) {
      createFollowupEvent({ event: submittedData });
      closeForm();
      return;
    }
    if (!isEmpty(defaultFieldValues)) {
      toggleModal(eventsNS);
      // that means it was opened from a detail panel of some other component
      closeForm();
    }
    createEvent({ event: submittedData });
  };

  const onEdit = vals => {
    const eventUUID = get(details, 'others.id.value');
    const submittedData = transformEventObject({
      ...vals,
      regionId,
      securityCompanyId,
      securityCompanyName,
    });
    updateEvent({
      id: eventUUID,
      event: {
        ...submittedData,
        parentEventId: parentEventId ? parentEventId : null,
        followupTasks: vals.followUptask.map(m => ({
          id: m.rootTaskId,
        })),
      },
      deletedfollowupEvents: vals.deletedfollowupEvents,
      deletedfollowUptask: vals.deletedfollowUptask,
    });
  };

  const values = editMode ? initialValues(details) : eventInitialState;

  return (
    <BaseForm
      size={size}
      imageRequired={false}
      allowToArchive={true}
      edit={editMode}
      onChange={handleFormValueUpdate}
      initialValues={values}
      formTitle={
        editMode
          ? staticText.operational_view.event.edit_default
          : followupEventTitle || staticText.operational_view.event.create
      }
      closeForm={closeForm}
      saveHoverText={
        editMode
          ? translatedKeyValueText('update_value_title', {
              value: details.generalInfo.title.value,
            })
          : staticText.operational_view.event.create
      }
      onSubmit={editMode ? onEdit : onCreate}
      validationSchema={validationSchema(startTime)}
      customSections={[
        {
          showSection: false,
          sectionText: '',
          fields: noSectionFields({
            eventCategory,
            editMode,
            services: subscribedCompanyServices,
            disabledFields: values.disabledFields,
          }),
        },
        {
          showSection: true,
          sectionText: 'Audit Trail',
          fields: auditTrailSection({ auditStatus }),
        },
        {
          showSection: true,
          sectionText: 'Overview',
          fields: overviewSection({ startTime, finishTime }),
        },
        {
          showSection: true,
          sectionText: 'Location',
          fields: locationSection({
            eventCategory,
            sitePolygon: eventSitePolygon,
            disabledFields: values.disabledFields,
          }),
        },
        {
          showSection: true,
          sectionText: 'Description',
          fields: descriptionSection(),
        },
        {
          showSection: true,
          sectionText: 'Description',
          fields: detailedSection({
            formValues: {
              media,
            },
            disabledFields: values.disabledFields,
            securityCompanyId,
          }),
        },
        {
          showSection: true,
          sectionText: 'Incident Review',
          fields: incidentReviewSection({
            editMode,
            parentEventId: get(details, 'others.id.value'),
            updatedValues: values,
          }),
        },
      ]}
      disablePresist={disablePresist} // this will only disable in followup form and not in the actual event form
      hoverText={editMode ? details.generalInfo.title.value : ''}
      note={EventInfo.note}
      enableReinitialize={false}
      formInfo={{
        link: HelpLinks.eventLink,
        value: HelpLinks.eventText,
      }}
      persistentStateName={persistentStateName}
    />
  );
};

EventsForm.propTypes = {
  creator: PropTypes.object,
  regionId: PropTypes.string,
  createEvent: PropTypes.func,
  addFollowupEventToState: PropTypes.func,
  updateEvent: PropTypes.func,
  securityCompanyId: PropTypes.string,
  securityCompanyName: PropTypes.string,
  editMode: PropTypes.bool,
  isFollowup: PropTypes.bool,
  details: PropTypes.object,
  editModeValues: PropTypes.object,
  closeForm: PropTypes.func,
  openEvent: PropTypes.func,
  toggleModal: PropTypes.func,
  followupEvents: PropTypes.array,
  parentEventId: PropTypes.string,
  noOfMediaFilesAllowed: PropTypes.number,
  subscribedProducts: PropTypes.object,
  subscribedCompanyServices: PropTypes.array,
  followupEventTitle: PropTypes.string,
  createFollowupEvent: PropTypes.func,
  disablePresist: PropTypes.bool,
  persistentStateName: PropTypes.string,
  size: PropTypes.string,
  defaultFieldValues: PropTypes.object,
};

const mapStateToProps = state => ({
  creator: state.user,
  regionId: getRegionId(state),
  securityCompanyId: getSecurityId(state),
  securityCompanyName: getSecurityCompanyName(state),
  details: eventSelectors.getEventDetailsForForms(state),
  noOfMediaFilesAllowed: totalMediafilesAllowed(state),
  subscribedCompanyServices: state.securityCompany.services,
  parentEventId: eventSelectors.getBaseEventParentID(state),
});

const mapDispatchToProps = dispatch => ({
  createEvent: ({ event }) =>
    dispatch(eventsGridActions.createRowEntry({ event })),
  updateEvent: ({
    id,
    event,
    deletedfollowupEvents,
    parentEventId,
    deletedfollowUptask,
  }) =>
    dispatch(
      eventsGridActions.updateRowEntry({
        id,
        event,
        deletedfollowupEvents,
        parentEventId,
        deletedfollowUptask,
      }),
    ),
  toggleModal: ns => dispatch(toggleModal(ns)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventsForm);
